import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { ApiService, AuthService, config } from '@fgb/core';
import { ProviderName } from './models/provider.enum';
import { InputField } from './models/fusion-input.model';

const FUSION_SSO_STATE_COOKIE = 'fusion_sso_state';

/** FusionAuth provider settings sourced from the runtime config's providerSettings. */
interface FusionAuthSettings {
  /** FusionAuth host, e.g. https://auth.example.com */
  issuer: string;
  /** The FusionAuth application client id. */
  clientId: string;
  /** Where FusionAuth returns the user after logout. Defaults to portalUrl + the logout redirectUrl ('/login'). */
  postLogoutRedirectUri?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FusionAuthService {
  constructor(private authService: AuthService, private api: ApiService, private cookieService: CookieService) {}

  login(successredirectPath: string = '/fa-callback', clubId: number = 1) {
    let loginUrlParameters: InputField[] = [];

    let redirectUrlParam: InputField = {
      FieldName: 'RedirectUrl',
      Value: `${config.portalUrl}${successredirectPath}`,
    };
    loginUrlParameters.push(redirectUrlParam);
    const state = Math.random().toString(36).substring(2, 9);
    let ssoModeParam: InputField = {
      FieldName: 'State',
      Value: state,
    };
    this.cookieService.set(FUSION_SSO_STATE_COOKIE, state, undefined, '/');
    loginUrlParameters.push(ssoModeParam);

    return this.api.post(`sso/JWT/GenerateUrl/${ProviderName.FusionAuth}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the fusion login page
   * and log the user in via the tokens returned
   * from the response.
   * @param jwtToken JWT Token returned from
   * fusion, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateJwt(code: string, clubId: number = 1) {
    if (this.authService.hasAccessToken()) {
      return of(true);
    }

    let loginUrlParameters: InputField[] = [];

    let codeParam: InputField = {
      FieldName: 'code',
      Value: code,
    };

    loginUrlParameters.push(codeParam);

    let ssoModeParam: InputField = {
      FieldName: 'State',
      Value: this.getSsoState(),
    };
    loginUrlParameters.push(ssoModeParam);

    return this.api.post(`sso/v2/oauth/${ProviderName.FusionAuth}/${clubId}`, loginUrlParameters).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('FARefreshToken', authResponse.auth.ProviderRefreshToken.toString());
          localStorage.setItem('FAAccessToken', authResponse.auth.ProviderAccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Log out of the portal and provider (FusionAuth).
   *
   * Revokes the Fortress tokens server side with a normal logout call, then
   * redirects the browser to the FusionAuth hosted `/oauth2/logout` endpoint.
   * The redirect is required because FusionAuth only clears its own SSO session
   * cookie when the browser is navigated to it - a fetch/AJAX call will not
   * clear the cookie, so the user would otherwise be silently signed back in.
   * After clearing its session FusionAuth returns the user to the
   * `post_logout_redirect_uri`.
   *
   * The provider tokens are not sent to the logout endpoint; they are only
   * cleared locally. The redirect runs whether or not the revoke call succeeds.
   *
   * The FusionAuth host and client id are read from the runtime config, e.g.
   * @example
   * providerSettings: [{
   *   providerId: 109,            // FusionAuth
   *   settings: {
   *     issuer: 'https://auth.example.com',
   *     clientId: 'YOUR_APPLICATION_CLIENT_ID',
   *     postLogoutRedirectUri: 'https://app.example.com/login' // optional, defaults to portalUrl + redirectUrl
   *   }
   * }]
   * @param redirectUrl portal path FusionAuth returns the user to when
   * postLogoutRedirectUri is not configured, default to '/login'.
   */
  logout(redirectUrl: string = '/login') {
    localStorage.removeItem('FARefreshToken');
    localStorage.removeItem('FAAccessToken');

    return this.authService.logout().pipe(
      finalize(() => {
        const providerLogoutUrl = this.buildProviderLogoutUrl(redirectUrl);
        if (providerLogoutUrl) {
          window.location.href = providerLogoutUrl;
        }
      })
    );
  }

  /**
   * Build the FusionAuth hosted logout URL from the provider settings in the
   * runtime config. Returns an empty string when the provider is not configured
   * so the caller can fall back to its normal in-app navigation.
   */
  private buildProviderLogoutUrl(redirectUrl: string): string {
    const providerConfig = config.providerSettings?.find((p) => p.providerId === (ProviderName.FusionAuth as any));
    const settings = providerConfig?.settings as FusionAuthSettings | undefined;
    if (!settings || !settings.issuer || !settings.clientId) {
      return '';
    }

    const params = new URLSearchParams({
      client_id: settings.clientId,
      post_logout_redirect_uri: settings.postLogoutRedirectUri || `${config.portalUrl}${redirectUrl}`,
    });

    const issuer = settings.issuer.replace(/\/+$/, '');
    return `${issuer}/oauth2/logout?${params.toString()}`;
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _fa_sso_upid cookie given
   * from FusionAuth domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let faDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      faDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${ProviderName.FusionAuth}/${faDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('FARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('FAAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Check if a named cookie exists.
   * @param cookieName cookie name to be checked.
   * @returns boolean value depending if cookie name
   * exists.
   */
  hasNamedCookie(cookieName: string): boolean {
    if (this.cookieService.check(cookieName)) {
      return true;
    }
    return false;
  }

  getSsoState(): string {
    return this.cookieService.get(FUSION_SSO_STATE_COOKIE);
  }
}
